import { motion } from "framer-motion";
import { useEffect } from "react";
import {
  FaDiscord,
  FaInstagram,
  FaXTwitter,
  FaTiktok,
  FaYoutube,
  FaEnvelope,
  FaPaypal,
} from "react-icons/fa6";
import { useTheme } from "../context/ThemeContext";
import Logo from "../components/Logo";
import MetaData from "../components/MetaData";

const Contact = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.style.scrollBehavior = "auto";
    document.body.style.scrollBehavior = "auto";
    return () => {
      document.documentElement.style.scrollBehavior = "smooth";
      document.body.style.scrollBehavior = "smooth";
    };
  }, []);

  const { darkMode } = useTheme();

  const socialLinks = [
    {
      icon: <FaDiscord size={24} />,
      text: "Join our Discord Server",
      href: "https://discord.gg/SZ53rVUJED",
    },
    {
      icon: <FaInstagram size={24} />,
      text: "Follow us on Instagram",
      href: "https://www.instagram.com/_.novacreations._/",
    },
    {
      icon: <FaXTwitter size={24} />,
      text: "Follow us on X",
      href: "https://x.com/NovaCreationsMC",
    },
    {
      icon: <FaTiktok size={24} />,
      text: "Follow us on TikTok",
      href: "https://www.tiktok.com/@novacreationsmc",
    },
    {
      icon: <FaYoutube size={24} />,
      text: "Subscribe on YouTube",
      href: "https://www.youtube.com/@NovaCreationsMC",
    },
    {
      icon: <FaEnvelope size={24} />,
      text: "novacreationsln@gmail.com",
      href: "mailto:novacreationsln@gmail.com",
    },
  ];

  const contactStructuredData = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    mainEntity: {
      "@type": "Organization",
      name: "Nova Creations",
      url: "https://www.novacreations.site",
      contactPoint: [
        {
          "@type": "ContactPoint",
          contactType: "customer service",
          email: "novacreationsln@gmail.com",
          url: "https://discord.gg/SZ53rVUJED",
          availableLanguage: "English",
          serviceArea: "Worldwide",
        },
      ],
      sameAs: [
        "https://discord.gg/SZ53rVUJED",
        "https://www.instagram.com/_.novacreations._/",
        "https://x.com/NovaCreationsMC",
        "https://www.tiktok.com/@novacreationsmc",
        "https://www.youtube.com/@NovaCreationsMC",
      ],
    },
  };

  return (
    <div id="Contact" className="min-h-screen  text-white">
      <MetaData
        title="Contact - Nova Creations | Get Your Custom Minecraft Build Quote"
        description="Contact Nova Creations for custom Minecraft building services. Get a quote for houses, castles, terrain, and maps. Join our Discord for fast response and professional service."
        keywords="contact nova creations, minecraft build quote, minecraft building services contact, discord minecraft builders, custom minecraft build inquiry"
        url="https://www.novacreations.site/contact"
        image="https://i.imgur.com/qsv10H9.png"
        structuredData={contactStructuredData}
      />
      <div className="absolute inset-0 -z-5">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: darkMode ? 1 : 0 }}
          style={{}}
          transition={{ duration: 0.7, ease: "easeInOut" }}
          className="fixed inset-0 bg-[linear-gradient(to_right,#2d3c61_1px,transparent_1px),linear-gradient(to_bottom,#2d3c61_1px,transparent_1px)] bg-[size:48px_48px] [mask-image:radial-gradient(ellipse_70%_100%_at_58%_0%,#4d4dff_0%,transparent_110%)]"
        />
      </div>
      <Logo />
      <div className="container mx-auto px-4 pt-32 pb-16">
        <motion.div
          className="max-w-2xl mx-auto"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{
            duration: 0.5,
            ease: "easeOut",
          }}
        >
          <h1 className="text-4xl font-minecraft mb-8 text-center drop-shadow-2xl">
            Contact
          </h1>

          <div className="bg-slate-600 bg-opacity-50 backdrop-blur-lg p-8 rounded-lg shadow-lg transition-all duration-1000 ease-in-out hover:shadow-cyan-600">
            <div className="mb-12">
              <h2 className="text-2xl font-minecraft mb-4">Get in Touch</h2>
              <p className="mb-6">
                Interested in working with us? Have questions about our
                services? We'd love to hear from you!
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {socialLinks.map((link, index) => (
                  <a
                    key={index}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 hover:text-cyan-300 transition-all duration-1000 hover:translate-x-2"
                  >
                    {link.icon}
                    <span>{link.text}</span>
                  </a>
                ))}
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-minecraft mb-4">
                Commision Process
              </h2>
              <div className="space-y-2">
                <p className="flex justify-between">
                  <span>1. Open a ticket in Discord</span>
                </p>
                <p className="flex justify-between">
                  <span>2. Answer a few quick questions.</span>
                </p>
                <p className="flex justify-between">
                  <span>
                    3. After answering the questions, someone will conduct an
                    "interview" with you to understand your vision, details, and
                    similar aspects.
                  </span>
                </p>
                <p className="flex justify-between">
                  <span>
                    4. Then, we move on to the initial estimate. We are often
                    open to negotiations.
                  </span>
                </p>
                <p className="flex justify-between mb-4">
                  <span>
                    5. Once the main person understands all the details, we can
                    proceed with payment.
                  </span>
                </p>
                <div>
                  <h3 className="text-2xl font-minecraft mb-4">
                    Payment Methods
                  </h3>

                  <p className="flex justify-between mb-2">
                    <FaPaypal size={24} />
                  </p>
                  <p className="text-sm text-gray-400 flex justify-between">
                    <span>
                      And other payment methods can be discussed in a ticket.
                    </span>
                  </p>
                </div>
              </div>
              <p className="mt-6 text-sm">
                Response times may vary. We typically respond within 24 hours
                during business hours.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Contact;
